import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import Logo from '../Logo';
import * as Header from './AppHeader.style';
/**
 * Application header component with navigation and branding
 * @param {object} props - Component props
 * @param {string} props.title - The title of the header
 * @param {Function} props.onBack - Callback function for back button
 * @param {Function} props.onClose - Callback function for close button
 * @param {Function} props.onHistory - Callback function for history button
 * @param {Function} props.onNewChat - Callback function for new chat button
 * @param {ReactNode} props.leftSlot - Left slot content
 * @param {ReactNode} props.rightSlot - Right slot content
 * @param {ReactNode} props.profileMenuSlot - Profile menu slot content
 * @param {boolean} props.sideMenuOpen - Whether the side menu is open
 * @returns {ReactElement} The AppHeader component
 */

export default function AppHeader({
  title = 'Zenbusiness Velo',
  onBack,
  onClose,
  leftSlot,
  rightSlot,
  profileMenuSlot,
  onHistory,
  onNewChat,
  sideMenuOpen
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const logoSrc = isTablet
    ? '/s2/logo/zb-velo-full-stacked-white.svg'
    : '/s2/logo/zb-velo-full-white.svg';
  const logoHeight = isTablet ? 36 : 19;

  const renderLeft = () => {
    if (leftSlot) return leftSlot;
    if (onBack) {
      return (
        <Header.Button onClick={onBack}>
          {isMobile ? (
            <ArrowBackIcon fontSize="small" />
          ) : (
            <Box alignItems="center" display="flex" gap={1}>
              <ArrowBackIcon fontSize="small" />
              <Text fontWeight="600">Back</Text>
            </Box>
          )}
        </Header.Button>
      );
    }

    if (onNewChat || onHistory) {
      return (
        <Box display="flex" gap={1}>
          {onHistory && (
            <Header.IconButton
              aria-label={sideMenuOpen ? 'Close history' : 'View history'}
              label={sideMenuOpen ? 'Close history' : 'View history'}
              onClick={onHistory}
            >
              {sideMenuOpen ? (
                <MenuOpenRoundedIcon fontSize="small" />
              ) : (
                <HistoryIcon fontSize="small" />
              )}
            </Header.IconButton>
          )}
          {onNewChat && (
            <Header.Button onClick={onNewChat}>
              {isMobile ? (
                <AddIcon fontSize="small" />
              ) : (
                <Box alignItems="center" display="flex" gap={1}>
                  <AddIcon fontSize="small" />
                  <Text fontWeight="600">New Chat</Text>
                </Box>
              )}
            </Header.Button>
          )}
        </Box>
      );
    }
    return <Header.LeftSpacer />;
  };

  const renderRight = () => {
    if (rightSlot) return rightSlot;
    if (onClose) {
      return (
        <Box alignItems="center" display="flex" gap={1}>
          {/* Profile menu slot - conditionally rendered by parent */}
          {profileMenuSlot}

          <Header.Button
            aria-label="Close chat"
            label="Close chat"
            variant="outlined"
            onClick={onClose}
          >
            {isMobile ? (
              <CloseIcon fontSize="small" />
            ) : (
              <Box alignItems="center" display="flex" gap={1}>
                <Text fontWeight="600">Close chat</Text> <CloseIcon fontSize="small" />
              </Box>
            )}
          </Header.Button>
        </Box>
      );
    }
    return <Header.LeftSpacer />;
  };

  return (
    <Header.Background>
      <Header.Root>
        <Header.LeftContainer>{renderLeft()}</Header.LeftContainer>
        <Header.Branding>
          <Logo alt={title} height={logoHeight} src={logoSrc} />
        </Header.Branding>
        <Header.RightContainer>
          <Header.Actions>{renderRight()}</Header.Actions>
        </Header.RightContainer>
      </Header.Root>
    </Header.Background>
  );
}

AppHeader.propTypes = {
  title: PropTypes.string,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onHistory: PropTypes.func,
  onNewChat: PropTypes.func,
  leftSlot: PropTypes.node,
  rightSlot: PropTypes.node,
  profileMenuSlot: PropTypes.node,
  sideMenuOpen: PropTypes.bool
};
